import React, { useState, useEffect } from "react";
import axios from "axios";
import "../Specialeventsscreen.css";

const Specialeventsscreen = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSubscribeDropdown, setShowSubscribeDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Pagination & Date Filtering
  const [currentPage, setCurrentPage] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const eventsPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get("/api/events/getallevents");
        setEvents(data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) {
      return { 
        dayOfWeek: 'TBA', 
        dayOfMonth: '?', 
        fullDate: 'Date To Be Announced' 
      };
    }
    
    const date = new Date(dateString);
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const dayOfMonth = date.getDate();
    const fullDate = date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
    return { dayOfWeek, dayOfMonth, fullDate };
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Download handlers - AXIOS WITH BLOB RESPONSE
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get('/api/events/download-calendar.pdf', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'little-heaven-events.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download calendar. Please try again.');
    }
  };

  const handleDownloadICS = async () => {
    try {
      const response = await axios.get('/api/events/calendar.ics', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'little-heaven-events.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ICS:', error);
      alert('Failed to download calendar file. Please try again.');
    }
  };

  // Filter events by search query AND date range
  const filteredEvents = events.filter(event => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      event.title.toLowerCase().includes(query) ||
      (event.description && event.description.toLowerCase().includes(query));
    
    // Date range filter
    if (startDate || endDate) {
      if (!event.date) return false;
      
      const eventDate = new Date(event.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      
      if (start && eventDate < start) return false;
      if (end && eventDate > end) return false;
    }
    
    return matchesSearch;
  });

  // Sort events by date
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date) - new Date(b.date);
  });

  // Pagination
  const totalPages = Math.ceil(sortedEvents.length / eventsPerPage);
  const startIndex = currentPage * eventsPerPage;
  const paginatedEvents = sortedEvents.slice(startIndex, startIndex + eventsPerPage);

  const groupEventsByMonth = (events) => {
    const grouped = {};
    
    events.forEach(event => {
      if (!event.date) {
        if (!grouped['Upcoming Events']) grouped['Upcoming Events'] = [];
        grouped['Upcoming Events'].push(event);
      } else {
        const date = new Date(event.date);
        const monthYear = date.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        });
        if (!grouped[monthYear]) {
          grouped[monthYear] = [];
        }
        grouped[monthYear].push(event);
      }
    });
    
    return grouped;
  };

  const groupedEvents = groupEventsByMonth(paginatedEvents);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const toggleSubscribeDropdown = () => {
    setShowSubscribeDropdown(!showSubscribeDropdown);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    if (showSubscribeDropdown) setShowSubscribeDropdown(false);
  };

  // Lightbox handlers
  const openLightbox = (imageUrl, eventTitle) => {
    setLightboxImage({ url: imageUrl, title: eventTitle });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'auto';
  };

  // Navigation handlers
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    setStartDate(today);
    setEndDate(today);
    setCurrentPage(0);
    setShowDatePicker(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(0);
    setShowDatePicker(false);
  };

  const handleDateSelection = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setCurrentPage(0);
      setTimeout(() => setShowDatePicker(false), 300);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSubscribeDropdown && !event.target.closest('.subscribe-dropdown-container')) {
        setShowSubscribeDropdown(false);
      }
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSubscribeDropdown, showDatePicker]);

  // Close lightbox with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [lightboxImage]);

  // Calendar component
  const Calendar = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    
    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();
      
      return { daysInMonth, startingDayOfWeek, year, month };
    };

    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected = 
        (startDate && date.toDateString() === startDate.toDateString()) ||
        (endDate && date.toDateString() === endDate.toDateString());
      const isInRange = startDate && endDate && date > startDate && date < endDate;
      const isToday = date.toDateString() === new Date().toDateString();
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''} ${isToday ? 'today' : ''}`}
          onClick={() => handleDateSelection(date)}
        >
          {day}
        </div>
      );
    }

    const previousMonth = () => {
      setCurrentMonth(new Date(year, month - 1));
    };

    const nextMonth = () => {
      setCurrentMonth(new Date(year, month + 1));
    };

    return (
      <div className="calendar">
        <div className="calendar-header">
          <button onClick={previousMonth} className="calendar-nav">‹</button>
          <span className="calendar-month">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={nextMonth} className="calendar-nav">›</button>
        </div>
        
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days}
        </div>

        <div className="calendar-actions">
          {(startDate || endDate) && (
            <button className="calendar-clear-btn" onClick={clearDateFilter}>
              Clear Filter
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="events-loading">
        <div className="spinner"></div>
        <p>Loading events...</p>
      </div>
    );
  }

  const dateRangeText = startDate && endDate
    ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
    : startDate
    ? `From ${formatDateShort(startDate)}`
    : 'Select Date Range';

  return (
    <div className="events-container">
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage.url} alt={lightboxImage.title} />
            <p className="lightbox-caption">{lightboxImage.title}</p>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <div className="events-top-nav">
        <button 
          className="nav-arrow" 
          aria-label="Previous"
          onClick={goToPreviousPage}
          disabled={currentPage === 0}
        >
          <svg viewBox="0 0 10 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.7 14.4l-1.5 1.5L.3 8 8.2.1l1.5 1.5L3.3 8l6.4 6.4z"/>
          </svg>
        </button>
        
        <button 
          className="nav-arrow" 
          aria-label="Next"
          onClick={goToNextPage}
          disabled={currentPage >= totalPages - 1}
        >
          <svg viewBox="0 0 10 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M.3 1.6L1.8.1 9.7 8l-7.9 7.9-1.5-1.5L6.7 8 .3 1.6z"/>
          </svg>
        </button>

        <button className="today-button" onClick={goToToday}>
          Today
        </button>

        <div className="date-picker-container">
          <button className="date-range-selector" onClick={toggleDatePicker}>
            <span>{dateRangeText}</span>
            <svg viewBox="0 0 10 7" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M1.008.609L5 4.6 8.992.61l.958.958L5 6.517.05 1.566l.958-.958z"/>
            </svg>
          </button>

          {showDatePicker && (
            <div className="date-picker-dropdown">
              <Calendar />
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="page-indicator">
            Page {currentPage + 1} of {totalPages}
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="events-search-section">
        <form className="events-search-form" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search for events"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search for events by keyword"
            />
            <svg 
              className="search-icon" 
              aria-hidden="true" 
              viewBox="0 0 16 16" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M11.164 10.133L16 14.97 14.969 16l-4.836-4.836a6.225 6.225 0 01-3.875 1.352 6.24 6.24 0 01-4.427-1.832A6.272 6.272 0 010 6.258 6.24 6.24 0 011.831 1.83 6.272 6.272 0 016.258 0c1.67 0 3.235.658 4.426 1.831a6.272 6.272 0 011.832 4.427c0 1.422-.48 2.773-1.352 3.875zM6.258 1.458c-1.28 0-2.49.498-3.396 1.404-1.866 1.867-1.866 4.925 0 6.791a4.774 4.774 0 003.396 1.405c1.28 0 2.489-.498 3.395-1.405 1.867-1.866 1.867-4.924 0-6.79a4.774 4.774 0 00-3.395-1.405z"
              />
            </svg>
          </div>
          <button type="submit" className="search-button">
            Find events
          </button>
        </form>
      </div>

      {/* Events List */}
      <div className="events-list">
        {paginatedEvents.length > 0 ? (
          Object.entries(groupedEvents).map(([month, monthEvents]) => {
            if (monthEvents.length === 0) return null;
            
            return (
              <div key={month}>
                <div className="month-separator">
                  <h2>{month}</h2>
                </div>

                {monthEvents.map((event) => {
                  const { dayOfWeek, dayOfMonth, fullDate } = formatDate(event.date);
                  
                  return (
                    <div key={event._id} className="event-row">
                      <div className="event-date-badge">
                        <span className="date-weekday">{dayOfWeek}</span>
                        <span className="date-day">{dayOfMonth}</span>
                      </div>

                      <div className="event-content">
                        <div className="event-details">
                          {/* DateTime at the very top */}
                          <div className="event-datetime-top">
                            <time dateTime={event.date || ''}>
                              {fullDate}{event.time && ` @ ${event.time}`}
                            </time>
                          </div>

                          {/* Title immediately after datetime */}
                          <h3 className="event-title">{event.title}</h3>

                          {/* Description */}
                          {event.description && (
                            <p className="event-description">{event.description}</p>
                          )}

                          {/* Price */}
                          {event.price && (
                            <div className="event-price">${event.price}</div>
                          )}

                          {/* Facebook Link */}
                          {event.link && (
                            <a 
                              href={event.link}
                              className="event-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Go to Facebook Event
                            </a>
                          )}
                        </div>

                        <div 
                          className="event-image" 
                          onClick={() => openLightbox(event.imageurl, event.title)}
                          role="button"
                          tabIndex={0}
                          aria-label={`View ${event.title} image in fullscreen`}
                        >
                          <img 
                            src={event.imageurl} 
                            alt={event.title}
                            loading="lazy"
                          />
                          <div className="image-zoom-indicator">
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                              <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div className="no-events-message">
            <p>No events found{searchQuery && ` matching "${searchQuery}"`}</p>
            {(searchQuery || startDate || endDate) && (
              <button 
                className="clear-search-button"
                onClick={() => {
                  setSearchQuery("");
                  clearDateFilter();
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Calendar Action Buttons */}
      <div className="calendar-actions">
        <button 
          className="calendar-action-button download-button"
          onClick={handleDownloadPDF}
        >
          Download calendar
        </button>

        <div className="subscribe-dropdown-container">
          <button 
            className="calendar-action-button subscribe-button"
            onClick={toggleSubscribeDropdown}
            aria-expanded={showSubscribeDropdown}
            aria-haspopup="true"
          >
            Subscribe to calendar
            <svg 
              className="dropdown-caret" 
              aria-hidden="true" 
              viewBox="0 0 10 7" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M1.008.609L5 4.6 8.992.61l.958.958L5 6.517.05 1.566l.958-.958z"
              />
            </svg>
          </button>

          {showSubscribeDropdown && (
            <div className="subscribe-dropdown-menu">
              <ul>
                <li>
                  <a 
                    href={`https://calendar.google.com/calendar/render?cid=webcal://${window.location.host}/api/events/calendar.ics`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Calendar
                  </a>
                </li>
                <li>
                  <a 
                    href={`webcal://${window.location.host}/api/events/calendar.ics`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Apple Calendar (iCal)
                  </a>
                </li>
                <li>
                  <a 
                    href={`https://outlook.office.com/calendar/0/addfromweb?url=https://${window.location.host}/api/events/calendar.ics&name=Little Heaven Events`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Outlook 365
                  </a>
                </li>
                <li>
                  <a 
                    href={`https://outlook.live.com/calendar/0/addfromweb?url=https://${window.location.host}/api/events/calendar.ics&name=Little Heaven Events`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Outlook Live
                  </a>
                </li>
                <li>
                  <button
                    onClick={handleDownloadICS}
                    className="dropdown-menu-button"
                  >
                    Download .ics file
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Music Licensing Notice */}
      <div className="music-licensing-notice">
        <p>
          <strong>Note:</strong> Musicians performing live at our events will only play original music or cover songs licensed through SESAC.
        </p>
      </div>

      {/* Footer */}
      <div className="events-footer">
        <h4>It is a high honor to be able to host your celebration with your loved ones.</h4>
        <p>
          Whether you are having a birthday party or a big celebration, let us host your event here! 
          Little Heaven Bed & Breakfast is secluded so you can enjoy your party to the biggest. 
          We have a conference room, a commercial kitchen with seating, outdoor space, a pond, 
          and of course six guest rooms with six guest bathrooms. Check out our conference room below! 
          Call the cabin to reserve your special day now.
        </p>
      </div>

      {/* Gallery Section */}
      <div className="events-gallery-section">
        <div className="galleryWrap">
          <div className="single">
            <img src="https://i.postimg.cc/BZy2Nps9/event.jpg" alt="Event space" />
          </div>
          <div className="single">
            <img src="https://i.postimg.cc/nhPPK5T8/eventtwoo.jpg" alt="Event setup" />
          </div>
          <div className="single">
            <img src="https://i.postimg.cc/N00QFWMW/eventthreee.jpg" alt="Event venue" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Specialeventsscreen;